import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Columns3, CalendarCheck, Package, ListTodo, CreditCard, Bell, Settings, UserCog, ScrollText, ShieldCheck, LogOut, Menu, X, Sprout, Loader2, FormInput } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  ["/admin/dashboard", "Dashboard", LayoutDashboard], ["/admin/customers", "Customers & Leads", Users], ["/admin/pipeline", "Lead Pipeline", Columns3],
  ["/admin/bookings", "Trial Bookings", CalendarCheck], ["/admin/products", "Products & Inventory", Package], ["/admin/tasks", "Follow-ups", ListTodo],
  ["/admin/payments", "Payments", CreditCard, "payments:read"], ["/admin/notifications", "Notifications", Bell],
  ["/admin/privacy", "Privacy Requests", ShieldCheck, "privacy:read"], ["/admin/audit", "Audit & Conversions", ScrollText, "audit:read"],
  ["/admin/team", "Team", UserCog, "users:write"], ["/admin/forms", "Form Builder", FormInput, "settings:write"], ["/admin/settings", "Settings", Settings, "settings:write"],
];

export default function AdminLayout() {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/admin/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user, pathname]);
  useEffect(() => setOpen(false), [pathname]);
  if (user === null) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  const items = NAV.filter(([, , , p]) => !p || can(p));
  return (
    <div className="min-h-screen bg-slate-50 font-body text-slate-800">
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 transform bg-[#0B130E] transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-16 items-center justify-between px-5">
          <Link to="/admin/dashboard" className="flex items-center gap-2 text-white"><span className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-700"><Sprout className="h-4 w-4" /></span><span className="font-display font-bold">Lawn Trial CRM</span></Link>
          <button className="text-slate-400 lg:hidden" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <nav className="space-y-0.5 px-3 py-2">
          {items.map(([to, l, I]) => (
            <NavLink key={to} to={to} data-testid={`admin-nav-${to.split("/")[2]}`} className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-emerald-700 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
              <I className="h-4 w-4" />{l}{l === "Notifications" && unread > 0 && <span className="ml-auto rounded-full bg-amber-500 px-2 text-xs font-bold text-white">{unread}</span>}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-slate-200 bg-white/90 px-4 backdrop-blur-md sm:px-6">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-menu-toggle"><Menu className="h-5 w-5" /></button>
          <Link to="/" target="_blank" className="hidden text-sm text-slate-500 hover:text-emerald-800 lg:block">View website ↗</Link>
          <div className="flex items-center gap-4">
            <Link to="/admin/notifications" className="relative" data-testid="admin-notification-bell"><Bell className="h-5 w-5 text-slate-600" />{unread > 0 && <span className="absolute -right-1.5 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-white" data-testid="admin-unread-count">{unread}</span>}</Link>
            <div className="text-right"><p className="text-sm font-semibold text-slate-900" data-testid="admin-user-name">{user.name}</p><p className="text-xs capitalize text-slate-500">{user.role}</p></div>
            <button onClick={logout} data-testid="admin-logout-button" className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-600 hover:text-rose-600" title="Log out"><LogOut className="h-4 w-4" /></button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
      {open && <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
    </div>
  );
}
